/**
 * Cost model stage (§5f) — the full, editable, provenance-tagged running-cost
 * model. A train is a locomotive (by class/traction) plus a set of carriages,
 * each costed separately; infrastructure is per mile by segment; and an explicit
 * overhaul/restoration provision captures the boiler-ticket liability a cash view
 * omits. Every editable number is a `Rate` (value + provenance + lock + basis),
 * so the on-screen chips and the PDF assumptions register are generated from data
 * rather than hand-listed. Replaces the old Costs settings-drawer section.
 */
import { useState } from 'react'
import type { CostBreakdown, CostModel, Provenance, Rate, SegmentInfraRate } from '../types'
import { Btn } from '../ui'
import { ModellingCosts, type CostActivity } from './ModellingCosts'
import type { CostConfig, CostLine, RollingStockInput, StudioCostsResponse } from '../costLines'

const PROV_LABEL: Record<Provenance, string> = { measured: 'measured', assumed: 'assumed', claude: 'Claude' }
const gbp0 = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`

const clone = (m: CostModel): CostModel => JSON.parse(JSON.stringify(m)) as CostModel

export interface CostMonthDays {
  key: string
  label: string
  days: number
  /**
   * THAT MONTH'S OWN modelled £/day by category, from its banked plan.
   *
   * Chris, 2026-08-25, looking at a table of zeros: "should I be suspicious that
   * the loco costs are not being calculated - look at the zero's?" He was right
   * to be. The table took the £/day of whichever month was ON SCREEN and
   * multiplied it across all nine columns — so with March open (correctly
   * modelling no trains) every month read £0. Each month must be priced from
   * its own plan (law 6). Absent when a month has not been built; the cell then
   * falls back to the on-screen month and SAYS so.
   */
  perDay?: Record<string, number>
}

export interface CostModelStageProps {
  rates: CostModel
  onChange: (m: CostModel) => void
  onMakeDefault: () => void
  defaultsBusy: boolean
  defaultsNote: string | null
  /** Whether the cost model is locked for the session — the gate that lets a slice
   *  be designed (MASTER_PLAN §6.1). */
  locked: boolean
  onToggleLocked: () => void
  /** The active design's per-DAY cost breakdown — powers the "as modelled"
   *  totals on each card and the monthly panel. Null until something is scored. */
  costs?: CostBreakdown | null
  /** Running days per month of the modelling period, from the applied running set. */
  monthDays?: CostMonthDays[]
  /** Persist a calibrated model as the railway default immediately — a
   *  master-baseline save must survive a reload (Chris, 2026-08-20: "when I
   *  updated these cost-by-day defaults they didn't save"). */
  onPersist?: (m: CostModel) => void
  /** ── Accounts-anchored cost model (the new primary mechanism) ── */
  basis: string
  basisOptions: { value: string; label: string }[]
  onBasisChange: (b: string) => void
  costActivity: CostActivity
  costConfig: CostConfig
  onCostConfigChange: (c: CostConfig) => void
  costData: StudioCostsResponse | null
  costLoading: boolean
  costErr: string | null
  legacySeed: CostLine[]
  /** The rolling-stock register's engines, running days and basis activity (ModellingCosts). */
  rolling?: RollingStockInput
  /** Costs (Pre-Modelling) = the basis P&L table and the rate cards;
   *  Costs (Results) = the table after the build (Chris, 2026-10-28). */
  view?: 'pre' | 'results'
}

// The five reconciling categories (they sum EXACTLY to the day total):
// loco = running + footplate + prep + overhaul provision · carriages = maint +
// cleaning + restoration provision · crew = guards + TTI + on-costs ·
// infra · overheads.
type CatKey = 'loco' | 'carriages' | 'crew' | 'infra' | 'overhead'
const CATS: { key: CatKey; label: string }[] = [
  { key: 'loco', label: 'Locomotives (fuel + running maint + footplate crews + overhaul prov.)' },
  { key: 'carriages', label: 'Carriages (maint + cleaning + restoration prov.)' },
  { key: 'crew', label: 'Train crew (guards + TTI + on-costs)' },
  { key: 'infra', label: 'Infrastructure (P-way + S&T + NR access)' },
  { key: 'overhead', label: 'Fixed overheads (boxes, stations, insurance, marketing)' },
]
export const catPerDay = (c: CostBreakdown): Record<CatKey, number> => ({
  loco: c.steamRun + c.dieselRun + c.footplate + c.prep + c.overhaulLoco,
  carriages: c.carriageRun + c.restoration,
  crew: c.guards + c.tti + c.onCost,
  infra: c.infra,
  overhead: c.overhead,
})

/** Scale every rate behind one category by `factor` — how a real monthly £
 *  from the accounts becomes the master baseline while the STRUCTURE
 *  (per-mile, per-turn, per-vehicle) is preserved. */
function scaleCategory(m: CostModel, cat: CatKey, factor: number, basis: string): void {
  const mark = (r: Rate) => { r.value = Math.round(r.value * factor * 100) / 100; r.prov = 'measured'; r.basis = basis }
  if (cat === 'loco') for (const lc of m.locos) { mark(lc.fuelPerMile); mark(lc.runMaintPerMile); mark(lc.overhaulPerMile); mark(lc.footplatePerTurn); mark(lc.prepDisposalPerTurn) }
  if (cat === 'carriages') { mark(m.carriage.runMaintPerVehDay); mark(m.carriage.cleaningPerVehDay); mark(m.carriage.restorationPerVehDay) }
  if (cat === 'crew') { mark(m.crew.guardPerTurn); mark(m.crew.ttiPerTurn) }
  if (cat === 'infra') for (const s of m.infra) { mark(s.pwayPerMile); mark(s.sandtPerMile); mark(s.nrAccessPerPath) }
  if (cat === 'overhead') { mark(m.overhead.signallingBoxPerDay); mark(m.overhead.stationStaffPerDay); mark(m.overhead.insurancePerDay); mark(m.overhead.marketingAdminPerDay) }
}

export function CostModelStage({ rates, onChange, onMakeDefault, defaultsBusy, defaultsNote, locked, onToggleLocked, costs, monthDays, onPersist, basis, basisOptions, onBasisChange, costActivity, costConfig, onCostConfigChange, costData, costLoading, costErr, legacySeed, rolling, view }: CostModelStageProps) {
  const perDay = costs ? catPerDay(costs) : null
  const periodDays = (monthDays ?? []).reduce((t, x) => t + x.days, 0)
  // Loco costing basis B (Chris, 2026-08-20): one all-in £ PER WORKING
  // (session) by traction, used in ALL the modelling — a 13-working day costs
  // 13 × the rate, so dropping any working saves the full rate immediately.
  // A stays the per-mile/per-turn rate-card decomposition. (The stored enum
  // value remains 'perTurn' for saved-payload compatibility.)
  const locoB = rates.locoCostMode === 'perTurn' && !!rates.locoPerTurn
  const totalWorkings = costs ? costs.diagrams.reduce((t, d) => t + Math.max(1, d.legs.length), 0) : 0
  const seedPerTurn = (): { steam: Rate; diesel: Rate } => {
    const mk = (tr: 'steam' | 'diesel'): Rate => {
      const lc = rates.locos.find((l) => l.traction === tr) ?? rates.locos[0]
      // Per WORKING: the rate card's per-mile block over one working's miles,
      // plus the diagram-level pay spread over the workings a diagram runs.
      const milesPerWorking = costs && totalWorkings > 0 ? (costs.steamMiles + costs.dieselMiles) / totalWorkings : 24
      const workingsPerDiagram = costs && costs.diagrams.length > 0 ? totalWorkings / costs.diagrams.length : 4
      const v = Math.round((milesPerWorking * (lc.fuelPerMile.value + lc.runMaintPerMile.value + lc.overhaulPerMile.value)
        + (lc.footplatePerTurn.value + lc.prepDisposalPerTurn.value) * (1 + rates.crew.onCostPct.value) / Math.max(1, workingsPerDiagram)) / 10) * 10
      return { value: v, prov: 'assumed', locked: false, basis: `seeded from the ${lc.klass} rate card at ~${Math.round(milesPerWorking)} mi/working — replace with the real all-in cost per working` }
    }
    return { steam: mk('steam'), diesel: mk('diesel') }
  }
  const setLocoMode = (mode: 'ratecard' | 'perTurn') => {
    const c = clone(rates)
    c.locoCostMode = mode
    if (mode === 'perTurn' && !c.locoPerTurn) c.locoPerTurn = seedPerTurn()
    // Uncommitted loco £ edits belong to basis A — drop them so a later save
    // can't silently back-solve the rate cards from a stale figure.
    if (mode === 'perTurn') {
      setDayEdits((prev) => { const n = { ...prev }; delete n.loco; return n })
      setYearEdits((prev) => { const n = { ...prev }; delete n.loco; return n })
      setMonthEdits((prev) => {
        const n = { ...prev }
        for (const k of Object.keys(n)) if (k.startsWith('loco|')) delete n[k]
        return n
      })
    }
    onChange(c)
  }
  const putPerTurn = (tr: 'steam' | 'diesel', v: number) => {
    if (!Number.isFinite(v) || v < 0) return
    const c = clone(rates)
    if (!c.locoPerTurn) return
    c.locoPerTurn[tr].value = v
    c.locoPerTurn[tr].prov = 'measured'
    c.locoPerTurn[tr].basis = 'all-in cost per working/session (Chris)'
    onChange(c)
  }
  /** A category's displayed £/day: live edit > pinned master baseline > modelled.
   *  The pin is what makes a saved calibration READ BACK exactly as typed —
   *  without it, the cell re-derives from whichever design is active and a
   *  saved £9,555 can come back as £10,2xx (Chris: "I still don't think this
   *  is saving"). Loco in basis B is always the computed per-turn total. */
  const effFor = (key: CatKey): number => {
    if (key === 'loco' && locoB) return perDay ? perDay.loco : 0
    const dayEdit = dayEdits[key]
    if (dayEdit != null) return dayEdit
    const yearEdit = yearEdits[key]
    if (yearEdit != null && periodDays > 0) return yearEdit / periodDays
    const pinned = rates.masterDay?.[key]
    if (pinned != null) return pinned
    return perDay ? perDay[key] : 0
  }
  /** Uncommitted monthly £ overrides, keyed `${category}|${monthKey}`. */
  const [monthEdits, setMonthEdits] = useState<Record<string, number | undefined>>({})
  /** Uncommitted £/operating-day overrides per category — drives every month. */
  const [dayEdits, setDayEdits] = useState<Record<string, number | undefined>>({})
  /** Uncommitted year-total £ overrides per category. */
  const [yearEdits, setYearEdits] = useState<Record<string, number | undefined>>({})
  /** "as modelled" chip for a card: £/day now, £/period once the running set gives real days. */
  const modelledChip = (cat: CatKey) => {
    if (!perDay) return <span className="sh-card-note">as modelled — appears once a timetable is scored</span>
    const d = perDay[cat]
    return (
      <span className="sh-card-note sh-mono">
        as modelled ≈ <strong>{gbp0(d)}/day</strong>{periodDays > 0 ? <> · <strong>{gbp0(d * periodDays)}</strong> over the period ({periodDays} running days)</> : null}
      </span>
    )
  }
  type Sel = (m: CostModel) => Rate
  const put = (sel: Sel, v: number) => { if (!Number.isFinite(v)) return; const c = clone(rates); sel(c).value = v; onChange(c) }
  const lock = (sel: Sel) => { const c = clone(rates); const r = sel(c); r.locked = !r.locked; onChange(c) }
  const putInfraNymr = (key: 'pwayPerMile' | 'sandtPerMile', v: number) => {
    if (!Number.isFinite(v)) return
    const c = clone(rates); c.infra.forEach((s: SegmentInfraRate) => { if (s.owner === 'nymr') s[key].value = v }); onChange(c)
  }
  const putNrAccess = (v: number) => {
    if (!Number.isFinite(v)) return
    const c = clone(rates); c.infra.forEach((s: SegmentInfraRate) => { if (s.owner === 'networkrail') s.nrAccessPerPath.value = v }); onChange(c)
  }

  const cell = (sel: Sel, step = 0.5) => {
    const r = sel(rates)
    return (
      <span className="sh-rate-cell">
        <input
          type="number" step={step} value={r.value} disabled={r.locked}
          className={`sh-rate-input sh-rate--${r.prov}`}
          onChange={(e) => put(sel, parseFloat(e.target.value))}
        />
        <button type="button" className={`sh-lock${r.locked ? ' sh-lock--on' : ''}`} title={r.locked ? 'Locked — frozen for the session' : 'Unlocked — click to freeze'} onClick={() => lock(sel)}>
          {r.locked ? '🔒' : '🔓'}
        </button>
      </span>
    )
  }

  const editLine = (label: string, sel: Sel, basis: string, opts?: { step?: number; pct?: boolean }) => {
    const r = sel(rates)
    return (
      <div className="sh-cost-line" key={label}>
        <div className="sh-cost-line-label">
          <span>{label}</span>
          <span className={`sh-prov sh-prov--${r.prov}`}>{PROV_LABEL[r.prov]}</span>
        </div>
        <span className="sh-rate-cell">
          <input
            type="number" step={opts?.step ?? 1} value={opts?.pct ? Math.round(r.value * 100) : r.value} disabled={r.locked}
            className={`sh-rate-input sh-rate--${r.prov}`}
            onChange={(e) => { const v = parseFloat(e.target.value); put(sel, opts?.pct ? v / 100 : v) }}
          />
          <button type="button" className={`sh-lock${r.locked ? ' sh-lock--on' : ''}`} title={r.locked ? 'Locked' : 'Unlocked'} onClick={() => lock(sel)}>
            {r.locked ? '🔒' : '🔓'}
          </button>
        </span>
        <div className="sh-cost-line-basis">{basis}</div>
      </div>
    )
  }

  const carr = rates.carriage
  const crew = rates.crew
  const oh = rates.overhead
  const nymrSeg = rates.infra.find((s) => s.owner === 'nymr')
  const nrSeg = rates.infra.find((s) => s.owner === 'networkrail')
  const carrSubtotal = carr.runMaintPerVehDay.value + carr.restorationPerVehDay.value + carr.cleaningPerVehDay.value
  const ohSubtotal = oh.signallingBoxPerDay.value + oh.stationStaffPerDay.value + oh.insurancePerDay.value + oh.marketingAdminPerDay.value

  return (
    <>
      <ModellingCosts
        data={costData} loading={costLoading} err={costErr}
        basis={basis} basisOptions={basisOptions} onBasisChange={onBasisChange}
        activity={costActivity} config={costConfig} onConfigChange={onCostConfigChange}
        onPersist={() => onPersist?.(rates)} defaultsBusy={defaultsBusy} legacySeed={legacySeed}
        rolling={rolling}
        view={view === 'results' ? 'modelled' : view === 'pre' ? 'source' : undefined}
      />

      {view !== 'results' && <>
      <details className="sh-card sh-mt">
        <summary style={{ cursor: 'pointer', padding: '10px 12px', fontWeight: 600 }}>
          Legacy rate-card cost model — the old cost tables (kept for reference; still drives the engine's physical facts)
        </summary>
      <div className="sh-prov-legend">
        <span className="stu-note">Provenance:</span>
        <span className="sh-prov sh-prov--measured">● measured</span>
        <span className="sh-prov sh-prov--assumed">● assumed</span>
        <span className="sh-prov sh-prov--claude">● Claude</span>
        <span className="sh-card-note">🔒 Locked = frozen for the session &amp; printed in the assumptions register</span>
      </div>

      <section className="sh-card sh-card--pad">
        <div className="sh-section-label">A train is a locomotive + a set of carriages — each costed separately</div>
        <div className="sh-concept">
          <span className="sh-concept-item sh-concept-item--loco">🚂 Locomotive · fuel + running maint + overhaul provision + footplate</span>
          <span className="sh-concept-plus">+</span>
          <span className="sh-concept-item sh-concept-item--carr">🚃 Carriage set · running maint + restoration provision + cleaning, per vehicle</span>
          <span className="sh-concept-plus">+</span>
          <span className="sh-concept-item sh-concept-item--infra">🛤 Infrastructure · costed per mile, segment by segment</span>
        </div>
      </section>

      {/* locomotive rate cards */}
      <section className="sh-card sh-mt">
        <div className="sh-card-head">
          <span className="sh-card-icon">🚂</span>
          <span className="sh-card-title">Locomotive rate cards — by class &amp; traction</span>
          <span className="sh-card-note">Steam and diesel carry very different running <em>and</em> long-term costs</span>
        </div>
        <div style={{ padding: '4px 12px 0' }}>{modelledChip('loco')}</div>
        {locoB && (
          <div className="sh-card-note" style={{ padding: '4px 12px 0' }}>
            Basis <strong>B</strong> is active — locomotives are costed at the all-in <strong>£ per working</strong> set in the
            monthly table below (steam {gbp0(rates.locoPerTurn!.steam.value)} · diesel/railcar {gbp0(rates.locoPerTurn!.diesel.value)}).
            These per-mile cards are kept but not used until you switch back to A.
          </div>
        )}
        <div className="stu-scroll">
          <table className="stu-table">
            <thead>
              <tr>
                <th>Class (example loco)</th>
                <th className="num">Fuel £/mi</th>
                <th className="num">Run maint £/mi</th>
                <th className="num">Overhaul prov £/mi</th>
                <th className="num">Footplate £/turn</th>
                <th className="num">Prep+disposal £/turn</th>
              </tr>
            </thead>
            <tbody>
              {rates.locos.map((lc, i) => (
                <tr key={lc.code}>
                  <td>
                    <span className={`sh-loco-tag sh-loco-tag--${lc.traction}`}>{lc.traction}</span>
                    <strong>{lc.klass} · {lc.code}</strong>
                  </td>
                  <td className="num">{cell((m) => m.locos[i].fuelPerMile, 0.5)}</td>
                  <td className="num">{cell((m) => m.locos[i].runMaintPerMile, 0.5)}</td>
                  <td className="num">{cell((m) => m.locos[i].overhaulPerMile, 0.5)}</td>
                  <td className="num">{cell((m) => m.locos[i].footplatePerTurn, 10)}</td>
                  <td className="num">{cell((m) => m.locos[i].prepDisposalPerTurn, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="sh-card-foot">
          Overhaul provision accrues the cost of the next heavy overhaul over the miles run — for steam a 10-year
          boiler ticket (~£650k), for diesel a power-unit rebuild. These are the numbers most easily forgotten and the
          ones a board will test.
        </div>
      </section>

      {/* other cost groups */}
      <div className="sh-cost-groups sh-mt">
        <section className="sh-card">
          <div className="sh-card-head">
            <span className="sh-card-icon">🚃</span>
            <span className="sh-card-title">Carriages (per vehicle / running day)</span>
            <span className="sh-card-subtotal">{gbp0(carrSubtotal)} / veh</span>
          </div>
          <div style={{ padding: '2px 12px 0' }}>{modelledChip('carriages')}</div>
          {editLine('Running maintenance', (m) => m.carriage.runMaintPerVehDay, 'bearings, brakes, gangways — per vehicle per running day', { step: 2 })}
          {editLine('Restoration provision', (m) => m.carriage.restorationPerVehDay, 'accrues the next body/underframe restoration (~£90k / 20 yr)', { step: 2 })}
          {editLine('Cleaning & servicing', (m) => m.carriage.cleaningPerVehDay, 'carriage cleaning, water, consumables', { step: 1 })}
        </section>

        <section className="sh-card">
          <div className="sh-card-head">
            <span className="sh-card-icon">👷</span>
            <span className="sh-card-title">Train crew (beyond footplate)</span>
            <span className="sh-card-subtotal">£/turn + {Math.round(crew.onCostPct.value * 100)}%</span>
          </div>
          <div style={{ padding: '2px 12px 0' }}>{modelledChip('crew')}</div>
          {editLine('Guard', (m) => m.crew.guardPerTurn, 'one per set in traffic, per turn', { step: 10 })}
          {editLine('On-train (TTI) / catering', (m) => m.crew.ttiPerTurn, 'revenue protection & on-board, per turn', { step: 10 })}
          {editLine('Employer on-costs', (m) => m.crew.onCostPct, 'NI + pension + holiday, applied to all pay', { step: 1, pct: true })}
        </section>

        <section className="sh-card sh-span2">
          <div className="sh-card-head">
            <span className="sh-card-icon">🛤</span>
            <span className="sh-card-title">Infrastructure — per mile, by segment</span>
            {nymrSeg && <span className="sh-card-subtotal">P-way £{nymrSeg.pwayPerMile.value}/mi · S&amp;T £{nymrSeg.sandtPerMile.value}/mi</span>}
          </div>
          <div style={{ padding: '2px 12px 0' }}>{modelledChip('infra')}</div>
          {nymrSeg && editLine('P-way / route-mile (NYMR)', (m) => m.infra.find((s) => s.owner === 'nymr')!.pwayPerMile, 'applied to all NYMR segments', { step: 5 })}
          {nymrSeg && (
            <div className="sh-cost-line">
              <div className="sh-cost-line-label">
                <span>S&amp;T / route-mile (NYMR)</span>
                <span className={`sh-prov sh-prov--${nymrSeg.sandtPerMile.prov}`}>{PROV_LABEL[nymrSeg.sandtPerMile.prov]}</span>
              </div>
              <span className="sh-rate-cell">
                <input type="number" step={5} value={nymrSeg.sandtPerMile.value} disabled={nymrSeg.sandtPerMile.locked}
                  className={`sh-rate-input sh-rate--${nymrSeg.sandtPerMile.prov}`}
                  onChange={(e) => putInfraNymr('sandtPerMile', parseFloat(e.target.value))} />
              </span>
              <div className="sh-cost-line-basis">applied to all NYMR segments</div>
            </div>
          )}
          {nrSeg && (
            <div className="sh-cost-line">
              <div className="sh-cost-line-label">
                <span>Network Rail access / path</span>
                <span className={`sh-prov sh-prov--${nrSeg.nrAccessPerPath.prov}`}>{PROV_LABEL[nrSeg.nrAccessPerPath.prov]}</span>
              </div>
              <span className="sh-rate-cell">
                <input type="number" step={10} value={nrSeg.nrAccessPerPath.value} disabled={nrSeg.nrAccessPerPath.locked}
                  className={`sh-rate-input sh-rate--${nrSeg.nrAccessPerPath.prov}`}
                  onChange={(e) => putNrAccess(parseFloat(e.target.value))} />
              </span>
              <div className="sh-cost-line-basis">Grosmont–Whitby · Network Rail access charge, not our P-way (Mode B)</div>
            </div>
          )}
        </section>

        <section className="sh-card sh-span2">
          <div className="sh-card-head">
            <span className="sh-card-icon">🏛</span>
            <span className="sh-card-title">Fixed operating overheads (per box / station / day)</span>
            <span className="sh-card-subtotal">{gbp0(ohSubtotal)} / day basis</span>
          </div>
          <div style={{ padding: '2px 12px 0' }}>{modelledChip('overhead')}</div>
          {editLine('Signalling & box opening', (m) => m.overhead.signallingBoxPerDay, 'per signal box opened, per operating day', { step: 10 })}
          {editLine('Station staffing', (m) => m.overhead.stationStaffPerDay, 'booking office + platform, per staffed station', { step: 10 })}
          {editLine('Insurance', (m) => m.overhead.insurancePerDay, 'public liability, per operating day', { step: 10 })}
          {editLine('Marketing / admin allocation', (m) => m.overhead.marketingAdminPerDay, 'apportioned per operating day', { step: 10 })}
        </section>
      </div>

      {/* Monthly cost by category — the modelled £ per month, editable so a
          real month from the accounts can become the MASTER cost baseline
          (Chris, 2026-08-20). Saving scales every rate behind the edited
          categories so the model reproduces that month exactly, while the
          per-mile / per-turn / per-vehicle structure is preserved. */}
      {perDay && (monthDays?.length ?? 0) > 0 && (
        <section className="sh-card sh-mt">
          <div className="sh-card-head">
            <span className="sh-card-icon">📅</span>
            <span className="sh-card-title">Monthly costs by category — as modelled, editable</span>
            <span className="sh-card-note">type a real month's £ from the accounts, then save it as the master baseline</span>
          </div>
          <div className="stu-scroll">
            <table className="stu-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="num">Costs / day</th>
                  {monthDays!.map((mo) => <th key={mo.key} className="num">{mo.label}<br /><span className="sh-dim">{mo.days} days</span></th>)}
                  <th className="num">Year total<br /><span className="sh-dim">{periodDays} days</span></th>
                </tr>
              </thead>
              <tbody>
                {CATS.map((cat) => {
                  // A per-day edit drives every month; a year edit implies a new
                  // per-day; a month edit stands alone. Display precedence:
                  // day edit > year edit > modelled.
                  const dayEdit = dayEdits[cat.key]
                  const yearEdit = yearEdits[cat.key]
                  const pinned = rates.masterDay?.[cat.key]
                  const effPerDay = effFor(cat.key)
                  // Each month is priced from ITS OWN banked plan where there is
                  // one; only an unbuilt month borrows the on-screen figure.
                  const perDayOf = (mo: CostMonthDays) => mo.perDay?.[cat.key] ?? effPerDay
                  const yearTotal = yearEdit ?? monthDays!.reduce((t, mo) => t + (monthEdits[`${cat.key}|${mo.key}`] ?? perDayOf(mo) * mo.days), 0)
                  const isLoco = cat.key === 'loco'
                  return (
                    <tr key={cat.key}>
                      <td>
                        {cat.label}
                        {isLoco && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}>
                            <Btn kind={locoB ? 'ghost' : 'gold'} small onClick={() => setLocoMode('ratecard')}
                              title="A — cost locos from the per-mile + per-turn rate cards above">A · £/day</Btn>
                            <Btn kind={locoB ? 'gold' : 'ghost'} small onClick={() => setLocoMode('perTurn')}
                              title="B — one all-in £ per working (session): a 13-working day costs 13 × the rate, in all the modelling">B · £/working</Btn>
                          </div>
                        )}
                      </td>
                      {isLoco && locoB ? (
                        <>
                          {/* Basis B: the per-turn rates ARE the model — the day/
                              month cells are what those rates cost this design. */}
                          <td className="num">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                              <label className="sh-mono" style={{ fontSize: 11 }}>S
                                <input type="number" step={50} style={{ width: 64, marginLeft: 3 }}
                                  className={`sh-rate-input sh-rate--${rates.locoPerTurn!.steam.prov}`}
                                  value={rates.locoPerTurn!.steam.value}
                                  onChange={(e) => putPerTurn('steam', parseFloat(e.target.value))} />
                              </label>
                              <label className="sh-mono" style={{ fontSize: 11 }}>D
                                <input type="number" step={50} style={{ width: 64, marginLeft: 3 }}
                                  className={`sh-rate-input sh-rate--${rates.locoPerTurn!.diesel.prov}`}
                                  value={rates.locoPerTurn!.diesel.value}
                                  onChange={(e) => putPerTurn('diesel', parseFloat(e.target.value))} />
                              </label>
                              <span className="sh-dim" style={{ fontSize: 10 }}>£/working{totalWorkings > 0 ? ` × ${totalWorkings}` : ''} · = {gbp0(perDay.loco)}/day</span>
                            </div>
                          </td>
                          {monthDays!.map((mo) => (
                            <td key={mo.key} className="num sh-mono">{gbp0(perDay.loco * mo.days)}</td>
                          ))}
                          <td className="num sh-mono"><strong>{gbp0(perDay.loco * periodDays)}</strong></td>
                        </>
                      ) : (
                        <>
                          <td className="num">
                            <input
                              type="number" step={10}
                              className={`sh-rate-input${dayEdit != null || pinned != null ? ' sh-rate--measured' : ''}`}
                              style={{ width: 78 }}
                              title={pinned != null && dayEdit == null ? 'Master baseline (saved) — the modelling was calibrated to this figure' : undefined}
                              value={dayEdit ?? Math.round(effPerDay)}
                              onChange={(e) => {
                                const n = parseFloat(e.target.value)
                                setDayEdits((prev) => ({ ...prev, [cat.key]: Number.isFinite(n) ? n : 0 }))
                              }}
                            />
                            {pinned != null && dayEdit == null && Math.abs(pinned - perDay[cat.key]) > Math.max(1, perDay[cat.key] * 0.02) && (
                              <div className="sh-dim" style={{ fontSize: 10 }}>as modelled {gbp0(perDay[cat.key])}/day</div>
                            )}
                          </td>
                          {monthDays!.map((mo) => {
                            const k = `${cat.key}|${mo.key}`
                            const v = monthEdits[k]
                            return (
                              <td key={mo.key} className="num">
                                <input
                                  type="number" step={100}
                                  className={`sh-rate-input${v != null ? ' sh-rate--measured' : ''}`}
                                  style={{ width: 92 }}
                                  value={v ?? Math.round(perDayOf(mo) * mo.days)}
                                  onChange={(e) => {
                                    const n = parseFloat(e.target.value)
                                    setMonthEdits((prev) => ({ ...prev, [k]: Number.isFinite(n) ? n : 0 }))
                                  }}
                                />
                              </td>
                            )
                          })}
                          <td className="num">
                            <input
                              type="number" step={1000}
                              className={`sh-rate-input${yearEdit != null ? ' sh-rate--measured' : ''}`}
                              style={{ width: 104, fontWeight: 700 }}
                              value={yearEdit ?? Math.round(yearTotal)}
                              onChange={(e) => {
                                const n = parseFloat(e.target.value)
                                setYearEdits((prev) => ({ ...prev, [cat.key]: Number.isFinite(n) ? n : 0 }))
                              }}
                            />
                          </td>
                        </>
                      )}
                    </tr>
                  )
                })}
                <tr>
                  <td><strong>Total</strong></td>
                  <td className="num sh-mono"><strong>{gbp0(CATS.reduce((t, cat) => t + effFor(cat.key), 0))}</strong></td>
                  {monthDays!.map((mo) => {
                    const tot = CATS.reduce((t, cat) => t + (monthEdits[`${cat.key}|${mo.key}`] ?? (mo.perDay?.[cat.key] ?? effFor(cat.key)) * mo.days), 0)
                    return <td key={mo.key} className="num sh-mono"><strong>{gbp0(tot)}</strong></td>
                  })}
                  <td className="num sh-mono"><strong>{gbp0(CATS.reduce((t, cat) => {
                    return t + (yearEdits[cat.key] ?? monthDays!.reduce((x, mo) => x + (monthEdits[`${cat.key}|${mo.key}`] ?? (mo.perDay?.[cat.key] ?? effFor(cat.key)) * mo.days), 0))
                  }, 0))}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="sh-card-foot" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {locoB && (
              <Btn kind="gold" small disabled={defaultsBusy} onClick={() => onPersist?.(rates)}>
                Save loco £/working as master baseline
              </Btn>
            )}
            {(Object.keys(dayEdits).length > 0 || Object.keys(yearEdits).length > 0) && (
              <Btn kind="gold" small onClick={() => {
                const c = clone(rates)
                for (const cat of CATS) {
                  const dayEdit = dayEdits[cat.key]
                  const yearEdit = yearEdits[cat.key]
                  const computedDay = perDay[cat.key]
                  if (computedDay <= 0) continue
                  if (dayEdit != null && dayEdit >= 0) {
                    scaleCategory(c, cat.key, dayEdit / computedDay, `set to ${gbp0(dayEdit)}/operating day (Chris, from accounts)`)
                    c.masterDay = { ...c.masterDay, [cat.key]: dayEdit }
                  } else if (yearEdit != null && yearEdit >= 0 && periodDays > 0) {
                    scaleCategory(c, cat.key, (yearEdit / periodDays) / computedDay, `calibrated to the season accounts (${gbp0(yearEdit)} over ${periodDays} running days)`)
                    c.masterDay = { ...c.masterDay, [cat.key]: Math.round(yearEdit / periodDays) }
                  }
                }
                onChange(c)
                onPersist?.(c)
                setDayEdits({}); setYearEdits({})
              }}>
                Save per-day / year figures as master baseline
              </Btn>
            )}
            {monthDays!.map((mo) => {
              const edited = CATS.filter((cat) => monthEdits[`${cat.key}|${mo.key}`] != null)
              if (edited.length === 0) return null
              return (
                <Btn key={mo.key} kind="gold" small onClick={() => {
                  const c = clone(rates)
                  for (const cat of edited) {
                    const target = monthEdits[`${cat.key}|${mo.key}`]!
                    const computed = perDay[cat.key] * mo.days
                    if (computed <= 0 || target < 0) continue
                    scaleCategory(c, cat.key, target / computed, `calibrated to ${mo.label} accounts (${gbp0(target)}/month over ${mo.days} running days)`)
                    c.masterDay = { ...c.masterDay, [cat.key]: Math.round(target / mo.days) }
                  }
                  onChange(c)
                  onPersist?.(c)
                  setMonthEdits((prev) => {
                    const next = { ...prev }
                    for (const cat of edited) delete next[`${cat.key}|${mo.key}`]
                    return next
                  })
                }}>
                  Save {mo.label} as master baseline ({edited.length} categor{edited.length === 1 ? 'y' : 'ies'})
                </Btn>
              )
            })}
            {(Object.keys(rates.masterDay ?? {}).length > 0 || locoB) && (
              <Btn kind="ghost" small onClick={() => {
                // Chris, 2026-08-20: "revert to Individual Cost panels to
                // update master costs table — that will take the costs from
                // the individual cost panels above this one." Unpin the master
                // figures, put locos back on basis A, and store that state as
                // the railway default so the revert sticks too.
                const c = clone(rates)
                delete c.masterDay
                c.locoCostMode = 'ratecard'
                onChange(c)
                onPersist?.(c)
                setDayEdits({}); setYearEdits({}); setMonthEdits({})
              }}>
                Revert to Individual Cost panels
              </Btn>
            )}
            <span className="sh-make-default-note">
              Type a <strong>Costs/day</strong> figure and every month recalculates from the running calendar's days; type a
              <strong> Year total</strong> and the per-day is derived from the period's {periodDays} running days; or type any
              single month from the accounts. <strong>Saving scales every rate behind the edited categories, PINS the figures
              here as the master costs table, and stores it all as the railway default</strong> — they survive reloads, read
              back exactly as typed, and every future session loads them. While figures are pinned this table is the master —
              use <em>Revert to Individual Cost panels</em> to unpin and derive it from the rate-card panels above again.
              On-costs follow pay automatically, so the crew category lands within a few percent rather than to the penny.
            </span>
          </div>
        </section>
      )}

      </details>

      <div className="sh-make-default">
        <Btn kind={locked ? 'gold' : 'primary'} small onClick={onToggleLocked}>
          {locked ? '🔒 Cost model locked for this session — unlock' : 'Lock cost model for this session'}
        </Btn>
        <Btn kind="ghost" small disabled={defaultsBusy} onClick={onMakeDefault}>
          {defaultsBusy ? 'Saving…' : 'Make these the railway default'}
        </Btn>
        <span className="sh-make-default-note">
          A slice cannot be <em>designed</em> until the cost model is locked (no skipped steps). Locking freezes it for
          the session and prints it in the assumptions register. Making it the railway default saves it for every future
          session. {defaultsNote ?? 'Currently using the shipped constants unless a default has been saved.'}
        </span>
      </div>
      </>}
    </>
  )
}
